import { icons } from 'assets/icons';
import logo from 'assets/images/navenio-landscape.svg';
import logoSmall from 'assets/images/navenio-small.svg';
import IconButton from 'components/Button/IconButton';
import UserInfoDropdown from 'components/Header/components/UserInfoDropdown/UserInfoDropdown';
import { useRootStore, useUserRolePermissionStore } from 'contexts/RootStore.context';
import { Horizontal, StretchSpacer, Vertical } from 'gls';
import useDevice from 'hooks/useDevice';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { routePath } from 'router/routePaths';
import CollapseButton from './CollapseButton';
import AssetManagementIcon from './icons/AssetManagementIcon';
import ListIcon from './icons/ListIcon';
import MapIcon from './icons/MapIcon';
import NavLinkItem from './NavLinkItem';
import { StyledAside, StyledMobileMenu, StyledNav, StyledTop } from './styled';
import useNavHooks from './useNavHooks';

export type navItemsType = {
  name: string;
  href: string;
  matchingPaths?: string[];
  hidden?: boolean;
  icon?: any;
  submenu?: navItemsType[];
};

type NavigationProps = {
  navCollapsedDefault?: boolean
}

const Navigation = observer(({ navCollapsedDefault = false }: NavigationProps): React.ReactElement => {
  const [userHasCollapsed, setUserHasCollapsed] = useState(false);
  const [navIsCollapsed, setNavIsCollapsed] = useState(userHasCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isMobileDevice, isTabletDevice } = useDevice();
  const { hasDevicesFeature } = useRootStore()
  const { canViewUsers, canManageAssets, canViewAssets } = useUserRolePermissionStore()

  const navItems: navItemsType[] = useMemo(() => {
    const menues: navItemsType[] = [];

    if (canViewAssets || canViewUsers) {
      menues.push({
        name: 'Map View',
        href: '/map',
        icon: MapIcon,
      })
    }
    if (canViewAssets) {
      menues.push({
        name: 'Assets',
        href: routePath.assetList,
        icon: ListIcon,
      })
    }
    if (hasDevicesFeature && canManageAssets) {
      menues.push({
        name: 'Devices',
        href: routePath.deviceList,
        icon: ListIcon,
      })
    }
    if (canViewUsers) {
      menues.push({
        name: 'Users',
        href: routePath.recentUsers,
        icon: ListIcon,
      })
    }
    if (canManageAssets && !isMobileDevice) {
      menues.push({
        name: 'Management',
        href: 'management',
        icon: AssetManagementIcon,
        submenu: [{
          name: 'Asset Management',
          href: routePath.assetManagement,
        },
        {
          name: 'Beacon Management',
          href: routePath.beaconManagement,
        }]
      })
    }

    return menues;

  }, [canViewAssets, canViewUsers, hasDevicesFeature, canManageAssets, isMobileDevice]);

  const { toggleNavOpen, activeHref, setActiveHref } = useNavHooks({
    userHasCollapsed,
    navCollapsedDefault,
    navIsCollapsed,
    setNavIsCollapsed,
    setUserHasCollapsed,
  });

  if (mobileMenuOpen) {
    return (
      <StyledMobileMenu role="navigation">
        <Vertical className="container" horizontalAlign="left" spacing={10}>
          <IconButton
            className="nav-close-button"
            icon={<icons.CloseIcon aria-label='Close Menu' width={15} height={15} />}
            onClick={() => setMobileMenuOpen(false)}
            ariaLabel="Close Menu"
          />
          <div className="nav-userinfo">
            <UserInfoDropdown onItemClick={() => setMobileMenuOpen(false)} />
          </div>
          <div className="nav-seperator"></div>
          <StyledNav>
            <ul className="nav-menu-list">
              {navItems.filter(link => !link.hidden).map((link) => {
                return (
                  <NavLinkItem
                    key={link.href}
                    link={link}
                    navIsCollapsed={false}
                    activeHref={activeHref}
                    setActiveHref={setActiveHref}
                    setNavIsCollapsed={setNavIsCollapsed}
                    setUserHasCollapsed={setUserHasCollapsed}
                    closeMobileMenu={() => setMobileMenuOpen(false)}
                  />
                );
              })}
            </ul>
          </StyledNav>
          <StretchSpacer />
        </Vertical>
      </StyledMobileMenu>
    );
  }

  if (isTabletDevice) {
    return (
      <StyledTop>
        <Horizontal spacing="auto" className="nav-container" horizontalAlign="center">
          <IconButton
            id="mobile-nav-button"
            ariaLabel="Open Menu"
            icon={<icons.MobileMenu aria-label='Open Menu' width={22} height={22} />}
            onClick={() => setMobileMenuOpen(true)}
          />
          <img src={logo} height="35px" alt="Navenio Logo" />
          <div className="nav-search">&nbsp;</div>
        </Horizontal>
      </StyledTop>
    );
  }

  return (
    <StyledAside className={navIsCollapsed ? '--collapsed' : ''}>
      <div className="nav-container">
        <div className="logo-container">
          <img src={navIsCollapsed ? logoSmall : logo} alt="Navenio Logo" />
        </div>

        <CollapseButton onClick={toggleNavOpen} />

        <StyledNav>
          <ul className="nav-menu-list">
            {navItems.map((link) => {
              return (
                <NavLinkItem
                  key={link.href}
                  link={link}
                  navIsCollapsed={navIsCollapsed}
                  activeHref={activeHref}
                  setActiveHref={setActiveHref}
                  setNavIsCollapsed={setNavIsCollapsed}
                  setUserHasCollapsed={setUserHasCollapsed}
                />
              );
            })}
          </ul>
        </StyledNav>
      </div>
    </StyledAside>
  );
});
export default Navigation;
